import { Menu, Tooltip, type MenuProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useFlattenedRoutes, usePathname } from "@/router/hooks";
import { useSettings } from "@/store/settingStore";
import { ThemeMode } from "#/enum";
import { useTier } from "@/store/userStore";
import { AppstoreOutlined, BarChartOutlined, DashboardFilled, DeploymentUnitOutlined, FileExcelFilled, FolderOutlined, GlobalOutlined, HomeFilled, LinkOutlined, LockOutlined, StarOutlined } from "@ant-design/icons";
import { isFeatureRestricted } from "@/utils/featureRestrictions";
import { SvgIcon } from "@/components/icon";
import { LuUsers } from "react-icons/lu";
import { RiRobot2Fill } from "react-icons/ri";
import agentIcon from '@/assets/images/robotDark.svg';
import agentLightIcon from '@/assets/images/robotLight.svg';
import axios from "axios";
import { socket } from "@/socket";

import { themeVars } from "@/theme/theme.css";
import { NAV_HORIZONTAL_HEIGHT } from "../config";

export default function NavHorizontal(props: Readonly<{readonly isBlocked?: boolean;}>) {
  const navigate = useNavigate();
  const pathname = usePathname();

  const flattenedRoutes = useFlattenedRoutes();
  const settings = useSettings();
  const { themeMode } = settings;
  const { VITE_APP_URL: URL } = import.meta.env;
  const tier = useTier();

  const role_id = localStorage.getItem("roleId");
  const userId = localStorage.getItem("userId");
  const [folderStructures, setFolderStructures] = useState();

  const fetchDashboardFolderTree = async () => {
    try {
      const response = await axios.get(`${URL}/api/dashboards/dashboards_filehandlingss/${userId}`);
      setFolderStructures(response.data || {});
    } catch (error) {
      console.error("Error fetching dashboards", error);
    }
  };

  useEffect(() => {
    fetchDashboardFolderTree();
  }, [userId]);

  // socket.io
  useEffect(() => {
    if (!socket) return;

    const refreshDashboards = () => {
      fetchDashboardFolderTree();
    };

    socket.on("dashboard_updated", refreshDashboards);
    socket.on("dashboard_created", refreshDashboards);
    socket.on("dashboard_structure_built", refreshDashboards);

    return () => {
      socket.off("dashboard_updated", refreshDashboards);
      socket.off("dashboard_created", refreshDashboards);
      socket.off("dashboard_structure_built", refreshDashboards);
    };
  }, [userId]);

  const DASHBOARD_PREFIX_MAP: Record<string, string> = {
    "VADB Dashboards": "VADB",
    "Excel Dashboards": "EXCEL",
    "PowerBI Dashboards": "BI",
    "Web Dashboards": "WEB",
    "AI Dashboards": "AI",
    "AI agent Master Dashboards": "AI",
    "AI agent-Master Dashboards": "AI",
    "AI agent Master": "AI",
    "AI agent-Master": "AI",
    "AI agent Others Dashboards": "AI",
    "AI agent-Others Dashboards": "AI",
    "AI agent Others": "AI",
    "AI agent-Others": "AI",
    "AI agent Child Dashboards": "AI",
    "AI agent-Child Dashboards": "AI",
    "AI agent Child": "AI",
    "AI agent-Child": "AI"
  };
  const getPrefixForKey = (key: string): string => {
    if (!key) return "AI";
    if (DASHBOARD_PREFIX_MAP[key]) return DASHBOARD_PREFIX_MAP[key];
    const lower = key.toLowerCase();
    if (lower.includes("ai agent") || lower.includes("ai dashboard") || lower.includes("ai")) return "AI";
    if (lower.includes("excel")) return "EXCEL";
    if (lower.includes("powerbi") || lower.includes("power bi") || lower.includes("bi")) return "BI";
    if (lower.includes("vadb")) return "VADB";
    if (lower.includes("web")) return "WEB";
    return "AI";
  };
  const DASHBOARD_ICONS = {
    VADB: <DashboardFilled />,
    EXCEL: <FileExcelFilled />,
    BI: <BarChartOutlined />,
    WEB: <GlobalOutlined />,
    AI: <RiRobot2Fill />
  };
  const slugify = (name: Readonly<string>) =>
  name.
  trim().
  toLowerCase().
  split(/[^a-z0-9]+/).
  filter(Boolean).
  join("-");

  const buildAllDashboardTypes = (yearObj: Readonly<any>) => {
    if (!yearObj || typeof yearObj !== "object") return [];
    return Object.entries(yearObj).
    filter(([_, val]) => Array.isArray(val) && (val as any[]).length > 0).
    flatMap(([dashboardTypeKey, folderStructures]: any) => {
      const prefix = getPrefixForKey(dashboardTypeKey) || "AI";

      return (folderStructures || []).map((d: Readonly<any>) => {
        const slug = slugify(d.name || "dashboard");
        return {
          label: <Tooltip placement="topLeft" title={`[${prefix}] ${d.name}`}> [{prefix}] {d.name}</Tooltip>,
          key: `/dashboard/${d.id}/${slug}`,
          icon: DASHBOARD_ICONS[prefix as keyof typeof DASHBOARD_ICONS],
          dashboardType: prefix
        };
      });
    });
  };

  const buildHorizAdminYearItems = (clientName: string, deptName: string, deptObj: any) =>
    Object.entries(deptObj?.Year || {}).map(([year, yearObj]) => ({
      label: year,
      key: `year-${clientName}-${deptName}-${year}`,
      icon: <FolderOutlined />,
      children: buildAllDashboardTypes(yearObj)
    }));
  const buildHorizAdminDeptItems = (clientName: string, clientObj: any) =>
    Object.entries(clientObj?.Departments || {}).map(([deptName, deptObj]: any) => ({
      label: deptName,
      key: `dept-${clientName}-${deptName}`,
      icon: <FolderOutlined />,
      children: buildHorizAdminYearItems(clientName, deptName, deptObj)
    }));
  const buildAdminDashboardMenu = (apiResponse: Readonly<any>) => {
    const treeObj = apiResponse?.tree || apiResponse;
    if (!treeObj || typeof treeObj !== "object") return [];
    return Object.entries(treeObj).map(([clientName, clientObj]: any) => ({
      label: clientName,
      key: `client-${clientName}`,
      icon: <FolderOutlined />,
      children: buildHorizAdminDeptItems(clientName, clientObj)
    }));
  };

  const buildClientYearItemsH = (deptName: string, deptObj: any) =>
    Object.entries(deptObj?.Year || {}).map(([year, yearObj]) => ({
      label: year,
      key: `year-${deptName}-${year}`,
      icon: <FolderOutlined />,
      children: buildAllDashboardTypes(yearObj)
    }));
  const buildClientDashboardMenu = (apiResponse: Readonly<any>) => {
    const treeObj = apiResponse?.tree || apiResponse;
    if (!treeObj || typeof treeObj !== "object") return [];
    const client: any = Object.values(treeObj)[0];
    return Object.entries(client?.Departments || {}).map(
      ([deptName, deptObj]: any) => ({
        label: deptName,
        key: `dept-${deptName}`,
        icon: <FolderOutlined />,
        children: buildClientYearItemsH(deptName, deptObj)
      })
    );
  };

  const dashboardMenuItems = useMemo(() => {
    if (!folderStructures) return [];
    const treeData = (folderStructures as any)?.tree || folderStructures;
    if (role_id === "1") {
      return buildAdminDashboardMenu(treeData);
    }
    return buildClientDashboardMenu(treeData);
  }, [folderStructures, role_id]);

  const adminList = useMemo(() => {
    return [
    {
      label: "Home",
      key: "/dashboard/dashboard-playgrounds",
      icon: <HomeFilled style={{ fontSize: "16px" }} />
    },
    {
      label: "Dashboards",
      key: "dashboards",
      icon: <DashboardFilled />,
      children: dashboardMenuItems
    },
    ["1", "2"].includes(role_id || "") && {
      label: "Management",
      icon: <SvgIcon icon="ic-management" className="ant-menu-item-icon" size="20" />,
      children: [
      {
        label: "Users Management",
        key: '/management/user',
        icon: <LuUsers />
      },
      {
        label: role_id === "2" ? "Department Management" : "Client Management",
        key: '/management/role',
        icon: <DeploymentUnitOutlined />
      },
      {
        label: "Feature Management",
        key: '/management/feature',
        icon: <AppstoreOutlined />
      }]

    },
    ["1"].includes(role_id || "") && {
      label: "Enterprise Management",
      icon: <SvgIcon icon="ic-management" className="ant-menu-item-icon" size="20" />,
      children: [
        {
          label: "Credit & Seat Management",
          key: '/management/credit-seat',
          icon: <DeploymentUnitOutlined />
        },
        {
          label: "Client Onboarding",
          key: '/management/client-onboarding',
          icon: <DeploymentUnitOutlined />
        },
      ]
    },
    ["1", "2", "3"].includes(role_id || "") && {
      label: "Configurations",
      icon: <SvgIcon icon="ic-menu" className="ant-menu-item-icon" size="24" />,
      children: [
      {
        label: "Dashboard Configuration",
        key: '/configuration/dashboard-management',
        icon: <DashboardFilled />
      },
      {
        label: "Widget Configuration",
        key: '/configuration/widjet-config',
        icon: <BarChartOutlined />
      },
      {
        label: "Datasource Configuration",
        key: '/configuration/datasource-config',
        icon: <FileExcelFilled />
      },
      {
        label: "External link Configuration",
        key: '/configuration/external-link-config',
        icon: <LinkOutlined />
      }]

    },
    ["1", "2"].includes(role_id || "") && {
      label: "AI Agents",
      icon: <img className="w-5 h-5 object-contain" src={themeMode == ThemeMode.Dark ? agentIcon : agentLightIcon} />,
      children: [
      {
        label:
        <>
								Merge Datasets
								{isFeatureRestricted(tier, 'data_merging') && <LockOutlined className="text-red-500 ml-2" />}
							</>,

        key: '/agents/merge-files',
        icon: <RiRobot2Fill />
      },
      {
        label: "Instant AI Dashboard",
        key: '/dashboard/instant-ai-dashboard',
        icon: <RiRobot2Fill />
      },
      {
        label: "VantageAI",
        key: '/agents/smart-dashboard-agent',
        icon: <RiRobot2Fill />
      }]

    }];

  }, [role_id, dashboardMenuItems, themeMode]);

  const menuListCustom = useMemo(() => {
    if (String(role_id) === "6") {
      return [
        {
          label: "Marketing Data",
          key: "/management/marketing-data",
          icon: <BarChartOutlined />
        },
        {
          label: "Enterprise Management",
          icon: <SvgIcon icon="ic-management" className="ant-menu-item-icon" size="20" />,
          children: [
            {
              label: "Client Onboarding",
              key: '/management/client-onboarding',
              icon: <DeploymentUnitOutlined />
            },
          ]
        }
      ];
    }
    return [
    {
      label: "Home",
      key: "/dashboard/dashboard-playgrounds",
      icon: <HomeFilled style={{ fontSize: "16px" }} />
    },
    {
      label: "Dashboards",
      key: "dashboards",
      icon: <DashboardFilled />,
      children: dashboardMenuItems
    },
    role_id === "2" && {
      label: "Management",
      icon: <SvgIcon icon="ic-management" className="ant-menu-item-icon" size="20" />,
      children: [
        {
          label: "User Management",
          key: "/management/user",
          icon: <LuUsers />
        },
        {
          label: "Credit Management",
          key: "/management/credits",
          icon: <StarOutlined />
        }
      ]
    },
    ["1", "2", "3"].includes(role_id || "") && {
      label: "Configurations",
      icon: <SvgIcon icon="ic-menu" className="ant-menu-item-icon" size="24" />,
      children: [
      {
        label: "Dashboard Configuration",
        key: '/configuration/dashboard-management',
        icon: <DashboardFilled />
      },
      {
        label: "Widget Configuration",
        key: '/configuration/widjet-config',
        icon: <BarChartOutlined />
      },
      {
        label: "Datasource Configuration",
        key: '/configuration/datasource-config',
        icon: <FileExcelFilled />
      },
      {
        label: "External link Configuration",
        key: '/configuration/external-link-config',
        icon: <LinkOutlined />
      }]

    },
    ["1", "2", "3", "4", "5"].includes(role_id || "") && {
      label: "AI Agents",
      icon: <img className="w-5 h-5 object-contain" src={themeMode == ThemeMode.Dark ? agentIcon : agentLightIcon} />,
      children: [
      {
        label:
        <>
								Merge Datasets
								{isFeatureRestricted(tier, 'data_merging') && <LockOutlined className="text-red-500 ml-2" />}
							</>,

        key: '/agents/merge-files',
        icon: <RiRobot2Fill />
      },
      {
        label: "Instant AI Dashboard",
        key: '/dashboard/instant-ai-dashboard',
        icon: <RiRobot2Fill />
      },
      {
        label: "VantageAI",
        key: '/agents/smart-dashboard-agent',
        icon: <RiRobot2Fill />
      }]

    }];

  }, [role_id, dashboardMenuItems, themeMode, tier]);

  const selectedKeys = useMemo(() => [pathname], [pathname]);

  const onClick: MenuProps["onClick"] = ({ key }) => {
    const nextLink = flattenedRoutes?.find((el) => el.key === key);
    if (nextLink?.link) {
      window.open(nextLink.link, "_blank");
      return;
    }
    if (nextLink?.hideTab && nextLink?.frameSrc) {
      window.open(nextLink?.frameSrc, "_blank");
      return;
    }
    navigate(key);
  };

  return (
    <div className="w-screen" style={{ height: NAV_HORIZONTAL_HEIGHT, backgroundColor: "#f2f0f1", pointerEvents: props.isBlocked ? 'none' : 'auto' }}>
			<Menu
        mode="horizontal"
        items={(role_id === "1" ? adminList : menuListCustom).filter(Boolean) as any[]}
        defaultOpenKeys={[]}
        selectedKeys={selectedKeys}
        onClick={onClick}
        className="!border-none custom-menu"
        style={{ background: themeVars.colors.background.default }} />
      
		</div>);

}